// hooks/useSortableTable.ts
import { useState, useCallback } from 'react';

type SortConfig<T> = {
  key: keyof T | null;
  direction: 'asc' | 'desc' | null;
};

export function useSortableTable<T>(initialData: T[], fetchData: (() => Promise<void>) | null) {
  const [data, setData] = useState<T[]>(initialData);
  const [sortConfig, setSortConfig] = useState<SortConfig<T>>({
    key: null,
    direction: null,
  });

  const compareValues = (valueA: any, valueB: any, direction: 'asc' | 'desc'): number => {
    // Gestion des valeurs nulles
    if (valueA === null) return 1;
    if (valueB === null) return -1;
    if (valueA === undefined) return 1;
    if (valueB === undefined) return -1;

    // Gestion des booléens
    if (typeof valueA === 'boolean' && typeof valueB === 'boolean') {
      return direction === 'asc' 
        ? (valueA === valueB ? 0 : valueA ? 1 : -1)
        : (valueA === valueB ? 0 : valueA ? -1 : 1);
    }

    // Gestion des nombres
    if (!isNaN(Number(valueA)) && !isNaN(Number(valueB))) {
      const numA = Number(valueA);
      const numB = Number(valueB);
      return direction === 'asc' ? numA - numB : numB - numA;
    }

    // Gestion des chaînes de caractères
    const strA = String(valueA).toLowerCase();
    const strB = String(valueB).toLowerCase();

    if (strA < strB) return direction === 'asc' ? -1 : 1;
    if (strA > strB) return direction === 'asc' ? 1 : -1;
    return 0;
  };

  // Utilisation de useCallback pour éviter de recréer cette fonction à chaque rendu
  const sortData = useCallback((key: keyof T) => {
    let direction: 'asc' | 'desc' | null = 'asc';

    if (sortConfig.key === key) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc';
      } else if (sortConfig.direction === 'desc') {
        direction = null;
      }
    }

    setSortConfig({ key, direction });

    if (direction === null) {
      // Vérifier si fetchData existe avant de l'appeler
      if (fetchData) {
        fetchData().catch(error => console.error("Error fetching data:", error));
      }
      return;
    }

    setData(prevData => [...prevData].sort((a, b) => {
      return compareValues(a[key], b[key], direction as 'asc' | 'desc');
    }));
  }, [sortConfig, fetchData]);

  // Utilisation de useCallback pour cette fonction également
  const getSortIndicator = useCallback((key: keyof T) => {
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'asc') return ' ↑';
      if (sortConfig.direction === 'desc') return ' ↓';
    }
    return '';
  }, [sortConfig]);

  // Mémorise setData pour éviter des re-renders inutiles
  const memoizedSetData = useCallback((newData: T[]) => {
    setData(newData);
  }, []);

  return {
    sortedData: data,
    setData: memoizedSetData,
    sortData,
    getSortIndicator,
  };
}